"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapTeacherSettingsForPins = exports.AssignmentMeta = void 0;
const lodash_1 = require("lodash");
const utils_1 = require("../../utils");
const common_1 = require("../common");
const AppError_1 = require("../errors/AppError");
const AssignmentSummary_1 = require("./AssignmentSummary");
var AssignmentMeta;
(function (AssignmentMeta) {
    AssignmentMeta.excludedComparisonPaths = ['createdAt', 'modifiedAt', 'summary.modifiedAt', 'migratedAt'];
    function scheduledFromOptions(options) {
        const startDate = options.startDate ? new Date(options.startDate).getTime() : undefined;
        const closeDate = options.closeDate ? new Date(options.closeDate).getTime() : undefined;
        const duration = options.duration;
        if (!startDate && !closeDate && !duration)
            return undefined;
        if (startDate && startDate < Date.now()) {
            throw new AppError_1.AppError('invalid-params', "start time can't be in the past");
        }
        return {
            startDate,
            closeDate,
            started: startDate ? false : undefined,
            closed: closeDate ? false : undefined,
            duration: options.duration,
        };
    }
    AssignmentMeta.scheduledFromOptions = scheduledFromOptions;
    function createFromLesson(assignmentId, type, ownerId, now, teachers, assignmentInfo, lessonData, options) {
        const { label, groupId, legacy, module, order } = options;
        const meta = {
            _id: assignmentId,
            status: 'open',
            type,
            groupId,
            module,
            order,
            label,
            lessonId: lessonData.lesson._id,
            products: lessonData.lesson.products || [],
            ownerId,
            createdAt: now,
            modifiedAt: now,
            usersWithAccess: teachers,
            assignmentInfo,
            summary: AssignmentSummary_1.AssignmentSummary.empty(),
            teacherActivityAt: now,
            scheduled: scheduledFromOptions(options),
            legacy,
            channel: lessonData.lesson.channel || undefined,
            teacherSettings: mapTeacherSettingsForPins(lessonData.pins),
            // NOTE: This should always be "v2" after the revamp of the homework with the new lesson data model.
            version: lessonData.lesson.version, // undefined for classic lessons and "v2" for V2 lessons.
        };
        return meta;
    }
    AssignmentMeta.createFromLesson = createFromLesson;
    /** creates an in memory clone with the updated data */
    // would be nice if we can merge this with the update fields in the future
    function update(meta, updates) {
        const cloned = (0, lodash_1.cloneDeep)(meta);
        const updateFields = AssignmentMeta.updateFields(updates);
        (0, lodash_1.forEach)(updateFields, (value, field) => (0, lodash_1.set)(cloned, field, value));
        return cloned;
    }
    AssignmentMeta.update = update;
    function newest(metas) {
        return (0, lodash_1.first)(orderByDate(metas));
    }
    AssignmentMeta.newest = newest;
    function orderByDate(metas) {
        return (0, lodash_1.orderBy)(metas, (meta) => meta.createdAt);
    }
    AssignmentMeta.orderByDate = orderByDate;
    function isTest(meta) {
        return meta.type === 'test';
    }
    AssignmentMeta.isTest = isTest;
    function isHomework(meta) {
        return meta.type === 'homework';
    }
    AssignmentMeta.isHomework = isHomework;
    function isRealtime(meta) {
        return meta.type === 'realtime';
    }
    AssignmentMeta.isRealtime = isRealtime;
    function isGroupAssignment(meta) {
        return !!meta.groupId;
    }
    AssignmentMeta.isGroupAssignment = isGroupAssignment;
    function hasProgress(meta) {
        return (meta.summary && meta.summary.percentagePinsDone > 0) || false;
    }
    AssignmentMeta.hasProgress = hasProgress;
    function isAnyUserInTestPhase(meta, phases) {
        return (meta.summary.phaseCounts && common_1.TestPhase.isAnyUserInTestPhase(meta.summary.phaseCounts, phases)) || false;
    }
    AssignmentMeta.isAnyUserInTestPhase = isAnyUserInTestPhase;
    function isLiveTest(meta) {
        const earliestPhase = meta.summary.phaseCounts && common_1.TestPhase.earliestTestPhase(meta.summary.phaseCounts);
        return common_1.TestPhase.isLive(earliestPhase) || false;
    }
    AssignmentMeta.isLiveTest = isLiveTest;
    function earliestPhase(meta) {
        return meta.summary.phaseCounts && common_1.TestPhase.earliestTestPhase(meta.summary.phaseCounts);
    }
    AssignmentMeta.earliestPhase = earliestPhase;
    function answer(meta, pinId) {
        const teacherSettings = teacherSettingsForPin(meta, pinId);
        // TODO: Introduce discrimated union type for teacher settings?
        if (teacherSettings && teacherSettings.correctAnswers) {
            return teacherSettings.correctAnswers;
        }
        return undefined;
    }
    AssignmentMeta.answer = answer;
    function hasCorrectAnswer(meta, pinId) {
        return answer(meta, pinId) !== undefined;
    }
    AssignmentMeta.hasCorrectAnswer = hasCorrectAnswer;
    function scheduledStartDate(meta) {
        if (!meta || !meta.scheduled || !meta.scheduled.startDate || meta.scheduled.started)
            return;
        return new Date(meta.scheduled.startDate);
    }
    AssignmentMeta.scheduledStartDate = scheduledStartDate;
    function scheduledCloseDate(meta) {
        if (!meta || !isTest(meta) || !meta.scheduled || !meta.scheduled.closeDate || meta.scheduled.closed)
            return;
        return new Date(meta.scheduled.closeDate);
    }
    AssignmentMeta.scheduledCloseDate = scheduledCloseDate;
    function scheduledDuration(meta) {
        if (!meta || !isTest(meta))
            return;
        return meta.scheduled?.duration;
    }
    AssignmentMeta.scheduledDuration = scheduledDuration;
    function schedulable(meta) {
        return {
            startDate: AssignmentMeta.scheduledStartDate(meta)?.getTime(),
            closeDate: AssignmentMeta.scheduledCloseDate(meta)?.getTime(),
        };
    }
    AssignmentMeta.schedulable = schedulable;
    function correctAnswerForPin(meta, pinId) {
        if (meta) {
            const correctAnswer = answer(meta, pinId);
            if (correctAnswer) {
                return correctAnswer;
            }
        }
        return undefined;
    }
    AssignmentMeta.correctAnswerForPin = correctAnswerForPin;
    function isChannelBased(meta) {
        return !!meta?.channel;
    }
    AssignmentMeta.isChannelBased = isChannelBased;
    function isProductBased(meta) {
        return !!meta?.products?.length;
    }
    AssignmentMeta.isProductBased = isProductBased;
    function teacherSettingsForPin(meta, pinId) {
        return meta.teacherSettings && meta.teacherSettings[pinId];
    }
    AssignmentMeta.teacherSettingsForPin = teacherSettingsForPin;
})(AssignmentMeta || (exports.AssignmentMeta = AssignmentMeta = {}));
(function (AssignmentMeta) {
    function updateFields(data) {
        const updateFields = {};
        if (!(0, lodash_1.isNil)(data.ownerId))
            updateFields['ownerId'] = data.ownerId;
        if (!(0, lodash_1.isNil)(data.summary))
            updateFields['summary'] = data.summary;
        if (!(0, lodash_1.isNil)(data.status))
            updateFields['status'] = data.status;
        if (!(0, lodash_1.isNil)(data.usersWithAccess))
            updateFields['usersWithAccess'] = data.usersWithAccess;
        if (!(0, lodash_1.isNil)(data.teacherActivityAt))
            updateFields['teacherActivityAt'] = data.teacherActivityAt;
        if (!(0, lodash_1.isUndefined)(data.startDate))
            updateFields['scheduled.startDate'] = data.startDate;
        if (!(0, lodash_1.isUndefined)(data.started))
            updateFields['scheduled.started'] = data.started;
        if (!(0, lodash_1.isUndefined)(data.startedDate))
            updateFields['scheduled.startedDate'] = data.startedDate;
        if (!(0, lodash_1.isUndefined)(data.closeDate))
            updateFields['scheduled.closeDate'] = data.closeDate;
        if (!(0, lodash_1.isUndefined)(data.closed))
            updateFields['scheduled.closed'] = data.closed;
        if (!(0, lodash_1.isUndefined)(data.closedDate))
            updateFields['scheduled.closedDate'] = data.closedDate;
        if (!(0, lodash_1.isUndefined)(data.duration))
            updateFields['scheduled.duration'] = data.duration;
        if (!(0, lodash_1.isUndefined)(data.raisedHandLog))
            updateFields['raisedHandLog'] = data.raisedHandLog;
        if (!(0, lodash_1.isNil)(data.module))
            updateFields['module'] = data.module;
        if (!(0, lodash_1.isNil)(data.order))
            updateFields['order'] = data.order;
        if (!(0, lodash_1.isNil)(data.pinAnswer) && data.pinAnswer.pinId) {
            updateFields[`answers.${data.pinAnswer.pinId}`] = data.pinAnswer.answer;
        }
        const cleaned = (0, utils_1.omitDeepUndefined)(updateFields);
        if (!(0, lodash_1.isEmpty)(cleaned)) {
            cleaned['modifiedAt'] = Date.now();
        }
        return cleaned;
    }
    AssignmentMeta.updateFields = updateFields;
})(AssignmentMeta || (exports.AssignmentMeta = AssignmentMeta = {}));
function mapTeacherSettingsForPins(pins) {
    const items = (0, lodash_1.map)(pins, (pin) => {
        return [pin.id, pin.teacherSettings];
    });
    return (0, lodash_1.fromPairs)((0, lodash_1.compact)(items));
}
exports.mapTeacherSettingsForPins = mapTeacherSettingsForPins;
//# sourceMappingURL=AssignmentMeta.js.map